// eslint-disable-next-line custom-config/prevent-chakra-import
import { css, Global } from '@emotion/react'
import { base, brand, system } from '@workday/canvas-tokens-web'
import { useAddCanvasThemeBodyAttribute, useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'
import vndlyColors, { brand as vndlyBrand } from '../colors'
import LegacyGlobalCSSVariables from './legacyWorkdayTheme'

/** Rebrand :root CSS variables (no feature-flag). Jest maps the module default to this component — see jestSetupFilesAfterEnv. */
export function SanaRebrandGlobalCSS() {
  return (
    <Global
      styles={css`
        /* THEMING VARIABLE DEFINITIONS */
        /**
        New VNDLY WORKDAY Theme
        These are the global design tokens used throughout the application. They are aligned with the
        tokens defined in the design system.

        Try to use these tokens where possible.
        **/

        :root {
          /* Colors */
          /* Navigation Menu */
          /* TODO: Remove this after Rebrand FF is removed */
          --vndly-color-navigation-menu-bar-background: var(${system.color.bg.default});

          /* Feedback includes alerts (banners, toasts and modals), tooltips, badges */
          --vndly-color-feedback-icon-info: var(${brand.primary600});
          --vndly-color-feedback-background-info: var(${base.blue100});
          --vndly-color-feedback-success: ${vndlyBrand[500]};
          --vndly-color-feedback-success-strong: ${vndlyBrand[700]};
          --vndly-color-feedback-icon-success: ${vndlyBrand[600]};
          --vndly-color-feedback-background-success: ${vndlyBrand[100]};
          --vndly-color-feedback-warning: ${vndlyColors.red[700]};
          --vndly-color-feedback-icon-warning: ${vndlyColors.red[700]};
          --vndly-color-feedback-background-warning: ${vndlyColors.yellow[200]};
          --vndly-color-feedback-danger: ${vndlyColors.red[600]};
          --vndly-color-feedback-danger-strong: ${vndlyColors.red[700]};
          --vndly-color-feedback-icon-danger: ${vndlyColors.red[600]};
          --vndly-color-feedback-background-danger: ${vndlyColors.red[100]};

          /* Button includes action buttons with either a primary, secondary or delete variant */
          --vndly-color-button: var(${system.color.fg.inverse});
          --vndly-color-button-background-primary: var(${brand.primary600});
          --vndly-color-button-background-primary-hover: var(${brand.primary700});
          --vndly-color-button-background-primary-active: var(${brand.primary800});
          --vndly-color-button-background-primary-hover-disabled: var(${brand.primary600});
          --vndly-color-button-secondary: var(${system.color.fg.strong});
          --vndly-color-button-background-secondary: var(${system.color.bg.default});
          --vndly-color-button-border-secondary: var(${system.color.border.input.default});
          --vndly-color-button-secondary-hover: var(${system.color.fg.stronger});
          --vndly-color-button-background-secondary-hover: var(${system.color.bg.alt.default});
          --vndly-color-button-border-secondary-hover: var(${system.color.border.input.hover});
          --vndly-color-button-background-secondary-active: var(${system.color.surface.overlay.hover.default});
          --vndly-color-button-tertiary: var(${brand.primary600});
          --vndly-color-button-tertiary-hover: var(${brand.primary700});
          --vndly-color-button-tertiary-background: var(${system.color.surface.transparent});
          --vndly-color-button-tertiary-background-hover: var(${system.color.surface.alt.default});
          --vndly-color-button-tertiary-background-active: var(${system.color.bg.alt.default});
          --vndly-color-button-background-delete-primary: var(${brand.critical600});
          --vndly-color-button-background-delete-primary-hover: var(${brand.critical700});
          --vndly-color-button-background-delete-primary-active: var(${brand.critical800});
          --vndly-color-button-background-delete-primary-disabled: var(${brand.critical600});
          --vndly-color-button-delete-secondary: var(${brand.critical600});
          --vndly-color-button-delete-secondary-hover: var(${system.color.fg.inverse});
          --vndly-color-button-delete-secondary-hover-disabled: var(${brand.critical600});
          --vndly-color-button-background-delete-secondary: var(${system.color.bg.default});
          --vndly-color-button-background-delete-secondary-hover: var(${brand.critical700});
          --vndly-color-button-background-delete-secondary-active: var(${brand.critical800});
          --vndly-color-button-border-delete-secondary: var(${system.color.border.contrast.default});
          --vndly-color-button-border-delete-secondary-hover: var(${brand.critical700});

          /* Link includes button or inline links */
          --vndly-color-link: var(${system.color.brand.fg.primary.default});
          --vndly-color-link-hover: var(${system.color.brand.fg.primary.strong});
          --vndly-color-link-background: var(${system.color.surface.transparent});
          --vndly-color-link-background-hover: var(${system.color.surface.alt.default});
          --vndly-color-link-inline-light: var(${system.color.fg.muted.default});
          --vndly-color-link-inline-light-hover: var(${system.color.fg.muted.strong});
          --vndly-color-link-inline-dark: ${vndlyColors.gray[800]}; // DEPRECATED
          --vndly-color-link-inline-dark-hover: ${vndlyColors.gray[900]}; // DEPRECATED
          --vndly-color-link-delete: var(${brand.critical600});
          --vndly-color-link-delete-hover: var(${brand.critical800});

          /* Tab includes nav and filter tabs */
          --vndly-color-tab-nav-text: var(${system.color.fg.muted.default});
          --vndly-color-tab-nav-text-hover: var(${system.color.fg.muted.strong});
          --vndly-color-tab-nav-text-selected: var(${system.color.brand.fg.primary.default});
          --vndly-color-tab-nav-background: transparent;
          --vndly-color-tab-nav-background-hover: var(${system.color.bg.alt.default});
          --vndly-color-tab-nav-border-hover: transparent;
          --vndly-color-tab-nav-border-selected: var(${brand.primary600});
          --vndly-color-tab-filter-text: ${vndlyColors.gray[500]};
          --vndly-color-tab-filter-background-hover: ${vndlyColors.gray[200]};
          --vndly-color-tab-filter-background-checked: ${vndlyColors.gray[200]};
          --vndly-color-tablist-border: ${vndlyColors.gray[300]};

          /* Input, Select */
          --vndly-color-input-text: var(${system.color.fg.default});
          --vndly-color-input-text-disabled: var(${system.color.fg.disabled});
          --vndly-color-input-text-hint: var(${system.color.fg.muted.default});
          --vndly-color-input-text-label: var(${system.color.fg.default});
          --vndly-color-input-text-required: var(${system.color.brand.fg.critical.default});
          --vndly-color-input-text-placeholder: var(${system.color.fg.muted.default});
          --vndly-color-input-background: var(${system.color.bg.default});
          --vndly-color-input-background-disabled: var(${system.color.surface.raised});
          --vndly-color-input-border: var(${system.color.border.input.default});
          --vndly-color-input-border-hover: var(${system.color.border.input.hover});
          --vndly-color-input-border-focused: var(${brand.common.focus});
          --vndly-color-input-border-invalid: var(${system.color.brand.border.critical});
          --vndly-color-input-border-warned: var(${system.color.brand.focus.caution.inner});
          --vndly-color-input-border-disabled: var(${system.color.fg.disabled});
          --vndly-color-input-shadow-focused: var(${brand.common.focus});
          --vndly-color-input-shadow-invalid: var(${system.color.brand.border.critical});
          --vndly-color-input-shadow-warned: var(${system.color.brand.focus.caution.inner});
          --vndly-color-input-addon-border: var(${system.color.border.strong});

          /* Checkbox, Radio */
          --vndly-color-input-icon: var(${system.color.fg.inverse});
          --vndly-color-input-icon-checked: var(${system.color.fg.inverse});
          --vndly-color-input-icon-indeterminate: var(${system.color.fg.inverse});
          --vndly-color-input-shadow-hover: var(${system.color.surface.alt.default});
          --vndly-color-input-background-checked: var(${brand.primary600});
          --vndly-color-input-background-checked-disabled: var(${brand.primary200});
          --vndly-color-input-background-checked-invalid: var(${brand.critical600});
          --vndly-color-input-background-checked-warned: var(${brand.caution400});
          --vndly-color-input-background-indeterminate: var(${brand.primary600});
          --vndly-color-input-background-indeterminate-disabled: var(${brand.primary200});
          --vndly-color-input-border-active: var(${system.color.border.input.hover});
          --vndly-color-input-border-checked: var(${brand.primary600});
          --vndly-color-input-border-checked-disabled: var(${brand.primary200});
          --vndly-color-input-border-indeterminate: var(${brand.primary600});
          --vndly-color-input-border-indeterminate-disabled: var(${brand.primary200});

          --vndly-color-listitem-background: transparent;
          --vndly-color-listitem-background-hover: var(${brand.primary25});
          --vndly-color-listitem-background-selected: var(${brand.primary100});
          --vndly-color-listitem-background-selected-hover: var(${brand.primary100});
          --vndly-color-listitem-border: var(${system.color.bg.alt.default});
          --vndly-color-listitem-selected-indicator-background: var(${brand.primary600});

          /* Container includes cards, drawers, popovers, modals and any general container properties such as backgrounds and text */
          --vndly-color-container-light: var(${system.color.bg.default});
          --vndly-color-container-dark: var(${system.color.fg.default});
          --vndly-color-container-subdued: var(${system.color.fg.muted.default});
          --vndly-color-container-background-light: var(${system.color.bg.default});
          --vndly-color-container-background-dark: var(${system.color.surface.contrast.default});
          --vndly-color-container-background-subdued: var(${system.color.surface.alt.default});
          --vndly-color-container-border: var(${system.color.border.strong});
          --vndly-color-container-icon: var(${system.color.fg.default});
          --vndly-color-container-icon-hover: var(${system.color.fg.muted.strong});

          --vndly-color-app-background: var(${system.color.bg.default});
          --vndly-color-container-border-focused: var(${brand.common.focus});
          --vndly-color-container-icon-info: var(${system.color.fg.info.default});
          --vndly-color-container-icon-danger: var(${system.color.brand.fg.critical.default});

          /* Typography */
          --vndly-font-family: Roboto, system-ui, sans-serif;
          --vndly-font-family-mono: 'Roboto Mono', 'Courier New', Courier, monospace;

          --vndly-font-weight-light: 300;
          --vndly-font-weight-normal: 400;
          --vndly-font-weight-semibold: 500;
          --vndly-font-weight-bold: 700;

          --vndly-font-size-sm: 0.875rem;
          --vndly-font-size-md: 1rem;
          --vndly-font-size-lg: 1.125rem;
          --vndly-font-size-xl: 1.25rem;
          --vndly-font-size-2xl: 1.5rem;
          --vndly-font-size-3xl: 1.875rem;
          --vndly-font-size-4xl: 2.25rem;

          --vndly-font-line-height-none: 1;
          --vndly-font-line-height-sm: 1.25;
          --vndly-font-line-height-md: 1.5; /* base */
        }
      `}
    />
  )
}

export default function GlobalCSSVariables() {
  useAddCanvasThemeBodyAttribute()
  return useIsSanaRebrandEnabled() ? <SanaRebrandGlobalCSS /> : <LegacyGlobalCSSVariables />
}
